import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import { IMultilingualContent, SupportedLanguage } from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { languageSchema } from "routes/event/schema";
import { z } from "zod";

interface TicketPhaseModalProps extends IModalProps {
  defaultValue?: IMultilingualContent;
  onSubmit: (data: IMultilingualContent) => void;
  languages: SupportedLanguage[];
}

const TicketPhaseSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    name: languageSchema(languages)
  }); 
}

export const TicketPhaseModal = ({
  open,
  onClose,
  defaultValue,
  onSubmit,
  languages,
}: TicketPhaseModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: IMultilingualContent }, HttpError, { name: IMultilingualContent }>({
    defaultValues: {
      name: !isEmpty(defaultValue) 
      ? defaultValue 
      : {
        en: "",
        vi: ""
      }
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        TicketPhaseSchema(languages)
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open) {
      reset({name: !isEmpty(defaultValue) ? defaultValue : {
        en: "",
        vi: ""
      }});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open]);

  const _submit = (values: { name: IMultilingualContent }) => {
    onSubmit(values.name);
    onClose();
  };

  return (
    <Modal title={`${!isEmpty(defaultValue) ? "Edit" : "Add"} ticket phase`} onClose={onClose} open={open}>
      <div>
        <div className="w-full divide-y divide-gray-100 text-sm">
          <div className="box-container">
            <Label
              required
              id={`phase_name`}
              name="Ticket phase name"
            />
            <div className="flex flex-col gap-y-4">
              {languages.map((lang) => (
                <div key={lang}>
                  <div className="relative">
                    <Input
                      label="Name"
                      id={`phase_name_${lang}`}
                      type="text"
                      placeholder="Ticket phase name"
                      className="pr-11"
                      error={get(errors, `name.${lang}`)}
                      {...register(`name.${lang}`)}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') {
                          handleSubmit(_submit)();
                        }
                      }}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(errors, `name.${lang}`) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `name.${lang}.message`)}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full box-container py-4">
            <div className="flex items-center justify-end gap-x-3">
              <Button
                className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={onClose}
              >
                CANCEL
              </Button>
              <Button className="w-20" onClick={handleSubmit(_submit)}>
                {!isEmpty(defaultValue) ? "UPDATE" : "ADD"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
};
